import { useQuoteDocumentEditable, useQuoteDocumentMode } from '@/components/quote-document/QuoteDocument';
import { setInvestmentRowCell, setInvestmentRowRate, setInvestmentRowShowHours, setInvestmentRowProductVersion } from '@/editor/investmentRowSettings';
import {
  duplicateInvestmentPhase,
  isQuoteWorkspacePreview,
  revealInvestmentPhase,
} from '@/editor/quoteWorkspace';
import {
  calculateInvestmentProduct,
  investmentProductRow,
  updateInvestmentProduct,
  type InvestmentProduct,
} from '@/editor/investmentProduct';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { closeHistory } from '@tiptap/pm/history';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DeleteInvestmentOverviewDialog from '@/components/editor/dialogs/DeleteInvestmentOverviewDialog';
import { uboTheme, themeColors, pricingTypes, type PricingType } from '@/theme/uboTheme';
import { useQuotePageBrand } from '@/store/quoteClientLogoContext';
import {
  useInvestmentInspectorStore,
  type InvestmentInspectorRow,
  type InvestmentRowVersionChange,
} from '@/store/investmentInspectorStore';
import { toast } from '@/lib/toast';
import { saveTableToLibrary } from '@/lib/investmentTableLibrary';
import { formatCurrency, isCurrencyAmount, parseEuroAmount } from '@/lib/format';
import type { InvestmentCurrency, InvestmentLabelLanguage, InvestmentTable } from '@/types';
import {
  buildInvestmentHeaderRowJson,
  getInvestmentTableLabels,
  type InvestmentTableLabelsResolved,
} from '@/editor/investmentTableLabels';
import { applyInvestmentGroupLabelsLanguage } from '@/editor/applyInvestmentGroupLabels';
import {
  investmentCellPlainText,
  investmentFirstCellRichHtml,
  investmentCellShouldStoreHtml,
  investmentTitleCellContent,
} from '@/editor/investmentTableCellContent';
import { emptyTableRowForPricing, rowHasHeaderCell } from '@/editor/investmentTableRow';
import {
  mergePricingRows,
  readPricingRows,
  rememberPricingRows,
} from '@/editor/investmentPricingSnapshots';
import { readInvestmentColumnWidths } from '@/editor/investmentColumnWidths';

type InvestmentPhaseRange = {
  from: number;
  to: number;
  startIndex: number;
  endIndex: number;
  tableId: string;
};

function buildTableForPricingType(pricingType: PricingType, L: InvestmentTableLabelsResolved) {
  const headerRow = buildInvestmentHeaderRowJson(pricingType, L);
  return {
    type: 'table',
    content: [headerRow, emptyTableRowForPricing(pricingType)],
  };
}

/** Nieuwe tabel-JSON met behoud van rijen (omschrijving + waar zinvol bedragen) bij pricingType-wissel. */
function buildInvestmentTableJsonPreservingRows(
  newType: PricingType,
  L: InvestmentTableLabelsResolved,
  preservedRows: InvestmentTable['rows'],
  currency: string
): Record<string, unknown> {
  const headerRow = buildInvestmentHeaderRowJson(newType, L);
  const rowsSource =
    preservedRows.length > 0
      ? preservedRows
      : ([{ id: 'row-empty', title: '' }] as InvestmentTable['rows']);

  const dataRows = rowsSource.map((row) => {
    if (row.investmentProduct)
      return investmentProductRow(row.investmentProduct, newType, currency);
    if (newType === 'hourly') {
      const price = row.price ?? 0;
      const hours = row.hours ?? 0;
      const sub = price * hours;
      const amountText = row.useText ? (row.text ?? '') : formatCurrency(price, currency);
      const subtotalText =
        row.subtotalText?.trim() || (row.useText ? amountText : formatCurrency(sub, currency));
      return {
        type: 'tableRow',
        attrs: { rateKey: row.rateKey ?? null, showHours: row.showHours ?? null },
        content: [
          { type: 'tableCell', content: investmentTitleCellContent(row) },
          {
            type: 'tableCell',
            content: [
              {
                type: 'paragraph',
                ...(amountText ? { content: [{ type: 'text', text: amountText }] } : {}),
              },
            ],
          },
          {
            type: 'tableCell',
            content: [
              {
                type: 'paragraph',
                ...(!row.useText && row.hours != null
                  ? { content: [{ type: 'text', text: String(hours) }] }
                  : {}),
              },
            ],
          },
          {
            type: 'tableCell',
            content: [
              {
                type: 'paragraph',
                ...(subtotalText ? { content: [{ type: 'text', text: subtotalText }] } : {}),
              },
            ],
          },
        ],
      };
    }
    if (newType === 'fixed') {
      return {
        type: 'tableRow',
        attrs: { rateKey: row.rateKey ?? null, showHours: row.showHours ?? null },
        content: [{ type: 'tableCell', content: investmentTitleCellContent(row) }],
      };
    }
    return {
      type: 'tableRow',
      attrs: { rateKey: row.rateKey ?? null, showHours: row.showHours ?? null },
      content: [
        { type: 'tableCell', content: investmentTitleCellContent(row) },
        {
          type: 'tableCell',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: row.useText
                    ? (row.text ?? '')
                    : formatCurrency(row.subtotal ?? 0, currency),
                },
              ],
            },
          ],
        },
      ],
    };
  });

  return { type: 'table', content: [headerRow, ...dataRows] };
}

function extractTableDataForLibrary(
  editor: NodeViewProps['editor'],
  getPos: NodeViewProps['getPos'],
  tableId: string,
  phaseTitle: string,
  pricingType: PricingType
) {
  if (!editor || typeof getPos !== 'function') return null;
  const pos = getPos();
  if (pos == null) return null;

  const resolved = editor.state.doc.resolve(pos);
  const parent = resolved.parent;

  let foundHeader = false;
  let tableNode: any = null;

  for (let i = 0; i < parent.childCount; i++) {
    const child = parent.child(i);
    if (child.type.name === 'investmentHeaderNode' && child.attrs.tableId === tableId) {
      foundHeader = true;
      continue;
    }
    if (foundHeader && child.type.name === 'table') {
      tableNode = child;
      break;
    }
  }

  if (!tableNode) return null;

  const rows: {
    title: string;
    titleHtml?: string;
    price?: number;
    hours?: number;
    subtotal?: number;
    text?: string;
    useText?: boolean;
    subtotalText?: string;
    investmentProduct?: InvestmentProduct;
    rateKey?: string;
    showHours?: boolean;
  }[] = [];
  tableNode.forEach((row: any) => {
    if (row.type.name !== 'tableRow') return;
    let hasHeader = false;
    row.forEach((cell: any) => {
      if (cell.type.name === 'tableHeader') hasHeader = true;
    });
    if (hasHeader) return;
    const rowSettings = {
      ...(row.attrs.rateKey ? { rateKey: String(row.attrs.rateKey) } : {}),
      ...(typeof row.attrs.showHours === 'boolean' ? { showHours: row.attrs.showHours } : {}),
    };

    if (row.attrs.investmentProduct) {
      const product = row.attrs.investmentProduct as InvestmentProduct;
      rows.push({
        title: product.productName,
        investmentProduct: product,
        subtotal: (calculateInvestmentProduct(product).totalCents ?? 0) / 100,
      });
      return;
    }

    const cells: string[] = [];
    const jsonCells: JSONContent[] = [];
    row.forEach((cell: PMNode) => {
      if (cell.type.name !== 'tableCell') return;
      jsonCells.push(cell.toJSON() as JSONContent);
    });

    jsonCells.forEach((jc) => {
      cells.push(investmentCellPlainText(jc));
    });

    const firstRich =
      jsonCells[0] && investmentCellShouldStoreHtml(jsonCells[0])
        ? investmentFirstCellRichHtml(jsonCells[0])
        : undefined;

    if (pricingType === 'hourly' && cells.length >= 3) {
      const priceInput = cells[1]?.trim() ?? '';
      const priceUsesText = priceInput !== '' && !isCurrencyAmount(priceInput);
      const subtotalInput = cells[3]?.trim() ?? '';
      const subtotalUsesText =
        subtotalInput !== '' &&
        !isCurrencyAmount(subtotalInput) &&
        (!priceUsesText || subtotalInput !== priceInput);
      const price = priceUsesText ? 0 : parseEuroAmount(priceInput);
      const hours = parseEuroAmount(cells[2]);
      rows.push({
        title: cells[0],
        ...rowSettings,
        ...(firstRich ? { titleHtml: firstRich } : {}),
        price,
        hours,
        subtotal: priceUsesText || subtotalUsesText ? 0 : price * hours,
        ...(priceUsesText ? { text: priceInput, useText: true } : {}),
        ...(subtotalUsesText ? { subtotalText: subtotalInput } : {}),
      });
    } else if (pricingType === 'fixed') {
      rows.push({ title: cells[0], ...rowSettings, ...(firstRich ? { titleHtml: firstRich } : {}), subtotal: 0 });
    } else {
      const amountInput = cells[1]?.trim() ?? '';
      const usesText = amountInput !== '' && !isCurrencyAmount(amountInput);
      rows.push({
        title: cells[0],
        ...rowSettings,
        ...(firstRich ? { titleHtml: firstRich } : {}),
        subtotal: usesText ? 0 : parseEuroAmount(amountInput),
        ...(usesText ? { text: amountInput, useText: true } : {}),
      });
    }
  });

  const out: InvestmentTable = {
    id: tableId,
    title: phaseTitle || 'Naamloos investeringsoverzicht',
    pricingType,
    rows: rows.map((r, i) => ({ id: `row-${i}`, ...r })),
    columnWidths: readInvestmentColumnWidths(tableNode.toJSON() as JSONContent, pricingType),
  };
  return out;
}

function investmentPhaseRanges(parent: PMNode, parentOffset: number): InvestmentPhaseRange[] {
  const childStarts: number[] = [];
  let offset = 0;
  for (let i = 0; i < parent.childCount; i++) {
    childStarts.push(parentOffset + offset);
    offset += parent.child(i).nodeSize;
  }

  const ranges: InvestmentPhaseRange[] = [];
  for (let i = 0; i < parent.childCount; i++) {
    const child = parent.child(i);
    if (child.type.name !== 'investmentHeaderNode' || child.attrs.isMainHeader) continue;

    let endIndex = i;
    for (let j = i + 1; j < parent.childCount; j++) {
      const next = parent.child(j);
      if (next.type.name === 'investmentHeaderNode') break;
      endIndex = j;
    }

    ranges.push({
      from: childStarts[i],
      to: childStarts[endIndex] + parent.child(endIndex).nodeSize,
      startIndex: i,
      endIndex,
      tableId: String(child.attrs.tableId ?? ''),
    });
  }

  return ranges;
}

function phaseNodes(parent: PMNode, range: InvestmentPhaseRange): PMNode[] {
  const nodes: PMNode[] = [];
  for (let i = range.startIndex; i <= range.endIndex; i++) {
    nodes.push(parent.child(i));
  }
  return nodes;
}

/**
 * Leest de datarij waar de cursor in staat, zodat de zijbalk kan tonen welke
 * regel je aanpast. Kopregels tellen niet mee; die zijn niet bewerkbaar.
 */
function readRowContext(doc: PMNode, head: number): InvestmentInspectorRow | null {
  if (head < 0 || head > doc.content.size) return null;
  const $head = doc.resolve(head);

  for (let depth = $head.depth; depth > 0; depth--) {
    const rowNode = $head.node(depth);
    if (rowNode.type.name !== 'tableRow') continue;
    if (rowHasHeaderCell(rowNode)) return null;

    const tableNode = $head.node(depth - 1);
    if (!tableNode || tableNode.type.name !== 'table') return null;

    const rowChildIndex = $head.index(depth - 1);
    let count = 0;
    let index = 0;
    for (let i = 0; i < tableNode.childCount; i++) {
      const child = tableNode.child(i);
      if (child.type.name !== 'tableRow' || rowHasHeaderCell(child)) continue;
      count++;
      if (i === rowChildIndex) index = count;
    }
    if (index === 0) return null;

    const cells: string[] = [];
    rowNode.forEach((cell) => cells.push(cell.textContent.trim()));

    return {
      rateKey: rowNode.attrs.rateKey ?? undefined,
      showHours: rowNode.attrs.showHours ?? undefined,
      index,
      count,
      title: cells[0] ?? '',
      cells,
      productId: (rowNode.attrs.productId as string | undefined) ?? null,
      productVersionId: (rowNode.attrs.productVersionId as string | undefined) ?? null,
      variantId: (rowNode.attrs.variantId as string | undefined) ?? null,
      quantity: (rowNode.attrs.quantity as number | undefined) ?? null,
      discountPct: (rowNode.attrs.discountPct as number | undefined) ?? null,
      hasOverrides: rowNode.attrs.hasOverrides === true,
      investmentProduct: rowNode.attrs.investmentProduct ?? undefined,
    };
  }

  return null;
}

export default function InvestmentHeaderView({
  node,
  editor,
  updateAttributes,
  getPos,
}: NodeViewProps) {
  const isEditable = useQuoteDocumentMode() === 'edit';
  const canEditText = useQuoteDocumentEditable();
  const pageBrand = useQuotePageBrand();
  const attrs = node.attrs as Record<string, unknown>;
  const isMainHeader = attrs.isMainHeader as boolean;
  const phaseHeaderRef = useRef<HTMLDivElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Een lege subtitel is geen regel: de klant ziet hem niet, dus de editor ook niet.
  const [addingSubtitle, setAddingSubtitle] = useState(false);
  const [subtitleFocused, setSubtitleFocused] = useState(false);
  const publishGroup = useInvestmentInspectorStore((state) => state.publishGroup);
  const clearGroup = useInvestmentInspectorStore((state) => state.clearGroup);
  const publishPhase = useInvestmentInspectorStore((state) => state.publishPhase);
  const clearPhase = useInvestmentInspectorStore((state) => state.clearPhase);

  const readGroupTheme = useCallback((): 'ubo' | 'ubx' => {
    if (!editor || typeof getPos !== 'function') return pageBrand;
    const pos = getPos();
    if (pos == null) return pageBrand;
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        const theme = n.attrs.theme as string | undefined;
        return theme === 'ubx' || theme === 'ubo' ? theme : pageBrand;
      }
    }
    return pageBrand;
  }, [editor, getPos, pageBrand]);

  const readGroupCurrency = useCallback((): InvestmentCurrency => {
    if (!editor || typeof getPos !== 'function') return 'EUR';
    const pos = getPos();
    if (pos == null) return 'EUR';
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        const c = n.attrs.currency as string | undefined;
        if (c === 'GBP' || c === 'USD') return c;
        return 'EUR';
      }
    }
    return 'EUR';
  }, [editor, getPos]);

  const readGroupLabelLanguage = useCallback((): InvestmentLabelLanguage => {
    if (!editor || typeof getPos !== 'function') return 'nl';
    const pos = getPos();
    if (pos == null) return 'nl';
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        const l = n.attrs.labelLanguage as string | undefined;
        return l === 'en' ? 'en' : 'nl';
      }
    }
    return 'nl';
  }, [editor, getPos]);

  const [currency, setCurrency] = useState<InvestmentCurrency>('EUR');
  const [labelLanguage, setLabelLanguage] = useState<InvestmentLabelLanguage>('nl');
  const [effectiveTheme, setEffectiveTheme] = useState<'ubo' | 'ubx'>(pageBrand);
  useEffect(() => {
    setCurrency(readGroupCurrency());
    setLabelLanguage(readGroupLabelLanguage());
    setEffectiveTheme(readGroupTheme());
  }, [readGroupCurrency, readGroupLabelLanguage, readGroupTheme]);

  useEffect(() => {
    if (!editor) return;
    const fn = () => {
      setCurrency(readGroupCurrency());
      setLabelLanguage(readGroupLabelLanguage());
      setEffectiveTheme(readGroupTheme());
    };
    editor.on('transaction', fn);
    return () => {
      editor.off('transaction', fn);
    };
  }, [editor, readGroupCurrency, readGroupLabelLanguage, readGroupTheme]);

  const handleCurrencyChange = useCallback(
    (next: string) => {
      const nextCur = (next === 'GBP' || next === 'USD' ? next : 'EUR') as InvestmentCurrency;
      if (!editor || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;
      const $pos = editor.state.doc.resolve(pos);
      for (let d = $pos.depth; d >= 1; d--) {
        const n = $pos.node(d);
        if (n.type.name === 'investmentGroupNode') {
          const from = $pos.before(d);
          const tr = editor.state.tr;
          tr.setNodeMarkup(from, undefined, { ...n.attrs, currency: nextCur });
          editor.view.dispatch(tr);
          setCurrency(nextCur);
          return;
        }
      }
    },
    [editor, getPos]
  );

  const handleLabelLanguageChange = useCallback(
    (next: string) => {
      const lang: InvestmentLabelLanguage = next === 'en' ? 'en' : 'nl';
      if (!editor || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;
      const $pos = editor.state.doc.resolve(pos);
      for (let d = $pos.depth; d >= 1; d--) {
        const n = $pos.node(d);
        if (n.type.name === 'investmentGroupNode') {
          const groupPos = $pos.before(d);
          applyInvestmentGroupLabelsLanguage(editor, groupPos, lang);
          setLabelLanguage(lang);
          return;
        }
      }
    },
    [editor, getPos]
  );

  const readShowTotalRowForPhase = useCallback((): boolean => {
    if (!editor || typeof getPos !== 'function') return true;
    const pos = getPos();
    if (pos == null) return true;
    const resolved = editor.state.doc.resolve(pos);
    const parent = resolved.parent;
    const tableId = attrs.tableId as string;
    let headerIdx = -1;
    for (let i = 0; i < parent.childCount; i++) {
      const child = parent.child(i);
      if (child.type.name === 'investmentHeaderNode' && child.attrs.tableId === tableId) {
        headerIdx = i;
        break;
      }
    }
    if (headerIdx < 0) return true;
    for (let i = headerIdx + 1; i < parent.childCount; i++) {
      const child = parent.child(i);
      if (child.type.name === 'investmentHeaderNode') break;
      if (child.type.name === 'investmentTotalNode' && child.attrs.tableId === tableId) {
        return child.attrs.showTotalRow !== false;
      }
    }
    return true;
  }, [editor, getPos, attrs.tableId]);

  const [showTotalRow, setShowTotalRow] = useState(true);
  useEffect(() => {
    setShowTotalRow(readShowTotalRowForPhase());
  }, [readShowTotalRowForPhase]);

  useEffect(() => {
    if (!editor) return;
    const fn = () => setShowTotalRow(readShowTotalRowForPhase());
    editor.on('transaction', fn);
    return () => {
      editor.off('transaction', fn);
    };
  }, [editor, readShowTotalRowForPhase]);

  const readPhasePosition = useCallback(() => {
    if (!editor || typeof getPos !== 'function') return { index: -1, count: 0 };
    const pos = getPos();
    if (pos == null) return { index: -1, count: 0 };

    const resolved = editor.state.doc.resolve(pos);
    const ranges = investmentPhaseRanges(resolved.parent, resolved.start(resolved.depth));
    const index = ranges.findIndex((range) => range.tableId === attrs.tableId);
    return { index, count: ranges.length };
  }, [editor, getPos, attrs.tableId]);

  const [phasePosition, setPhasePosition] = useState(() => readPhasePosition());
  useEffect(() => {
    setPhasePosition(readPhasePosition());
  }, [readPhasePosition]);

  useEffect(() => {
    if (!editor) return;
    const fn = () => setPhasePosition(readPhasePosition());
    editor.on('transaction', fn);
    return () => {
      editor.off('transaction', fn);
    };
  }, [editor, readPhasePosition]);

  const handleShowTotalRowChange = useCallback(
    (checked: boolean) => {
      if (!editor || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;
      const resolved = editor.state.doc.resolve(pos);
      const parent = resolved.parent;
      const parentOffset = resolved.start(resolved.depth);
      const tableId = attrs.tableId as string;

      let headerIdx = -1;
      for (let i = 0; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === 'investmentHeaderNode' && child.attrs.tableId === tableId) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) return;

      for (let i = headerIdx + 1; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === 'investmentHeaderNode') break;
        if (child.type.name === 'investmentTotalNode' && child.attrs.tableId === tableId) {
          let totalOffset = 0;
          for (let j = 0; j < i; j++) {
            totalOffset += parent.child(j).nodeSize;
          }
          const totalPos = parentOffset + totalOffset;
          const tr = editor.state.tr;
          tr.setNodeMarkup(totalPos, undefined, { ...child.attrs, showTotalRow: checked });
          editor.view.dispatch(tr);
          setShowTotalRow(checked);
          return;
        }
      }
    },
    [editor, getPos, attrs.tableId]
  );

  const mainTitle = (attrs.mainTitle as string) || 'Investeringsoverzicht';
  const phaseTitle = (attrs.phaseTitle as string) || '';
  const phaseSubtitle = (attrs.phaseSubtitle as string) || '';
  const pricingType = (attrs.pricingType as PricingType) || 'fixed';
  const titleColor = themeColors(effectiveTheme).primary;

  const handleDeletePhase = useCallback(() => {
    if (isEditable || isQuoteWorkspacePreview(editor)) setDeleteDialogOpen(true);
  }, [editor, isEditable]);

  const handleConfirmDeletePhase = useCallback(() => {
    if (
      editor.isDestroyed ||
      (!isEditable && !isQuoteWorkspacePreview(editor)) ||
      typeof getPos !== 'function'
    )
      return;
    const pos = getPos();
    if (pos == null) return;

    const resolved = editor.state.doc.resolve(pos);
    const parent = resolved.parent;
    if (parent.type.name !== 'investmentGroupNode') return;
    const range = investmentPhaseRanges(parent, resolved.start(resolved.depth)).find(
      (phase) => phase.tableId === attrs.tableId
    );
    if (!range) return;

    setDeleteDialogOpen(false);
    editor.view.dispatch(
      closeHistory(editor.state.tr).deleteRange(range.from, range.to).scrollIntoView()
    );
    editor.commands.focus();
    toast.success('Investeringsoverzicht verwijderd');
  }, [editor, getPos, attrs.tableId, isEditable]);

  const handleDeleteDialogClose = useCallback(() => {
    if (!editor.isDestroyed) editor.commands.focus();
  }, [editor]);

  const handleMovePhase = useCallback(
    (direction: 'up' | 'down') => {
      if (!editor || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;

      const resolved = editor.state.doc.resolve(pos);
      const parent = resolved.parent;
      const ranges = investmentPhaseRanges(parent, resolved.start(resolved.depth));
      const currentIndex = ranges.findIndex((range) => range.tableId === attrs.tableId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ranges.length) return;

      const current = ranges[currentIndex];
      const target = ranges[targetIndex];
      const currentNodes = phaseNodes(parent, current);
      const targetNodes = phaseNodes(parent, target);
      const from = direction === 'up' ? target.from : current.from;
      const to = direction === 'up' ? current.to : target.to;
      const replacement =
        direction === 'up' ? [...currentNodes, ...targetNodes] : [...targetNodes, ...currentNodes];

      editor.view.dispatch(editor.state.tr.replaceWith(from, to, replacement).scrollIntoView());
      editor.commands.focus();
    },
    [editor, getPos, attrs.tableId]
  );

  const handleDuplicatePhase = useCallback(() => {
    if (!editor) return;
    try {
      const copy = duplicateInvestmentPhase(editor, String(attrs.tableId));
      revealInvestmentPhase(editor, copy.tableId);
      toast.success(`${copy.title} toegevoegd`, {
        description: 'De kopie staat direct onder het origineel en is geselecteerd.',
      });
    } catch (error) {
      toast.error('Dupliceren is niet gelukt', { error });
    }
  }, [editor, attrs.tableId]);

  const handlePricingTypeChange = useCallback(
    (newType: string) => {
      if (!editor || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;

      const resolved = editor.state.doc.resolve(pos);
      const parent = resolved.parent;
      const parentOffset = resolved.start(resolved.depth);

      let headerIdx = -1;
      for (let i = 0; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === 'investmentHeaderNode' && child.attrs.tableId === attrs.tableId) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) return;

      let tableIdx = -1;
      let totalIdx = -1;
      for (let i = headerIdx + 1; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === 'table' && tableIdx < 0) {
          tableIdx = i;
        } else if (child.type.name === 'investmentTotalNode' && totalIdx < 0) {
          totalIdx = i;
          break;
        } else if (child.type.name === 'investmentHeaderNode') {
          break;
        }
      }

      if (tableIdx < 0) return;

      const { tr } = editor.state;

      const headerNode = parent.child(headerIdx);
      const oldPt =
        ((headerNode.attrs as Record<string, unknown>).pricingType as PricingType) || 'fixed';
      const preservedSnapshot = extractTableDataForLibrary(
        editor,
        getPos,
        attrs.tableId as string,
        phaseTitle,
        oldPt
      );
      const liveRows = preservedSnapshot?.rows ?? [];

      // Onthoud de huidige stand en haal terug wat het doelmodel nodig heeft: na
      // een omweg via "vast bedrag" staan uren en tarief nergens meer in het document.
      const tableIdValue = attrs.tableId as string;
      rememberPricingRows(tableIdValue, oldPt, liveRows);
      const preservedRows = mergePricingRows(
        newType as PricingType,
        liveRows,
        readPricingRows(tableIdValue, newType as PricingType)
      );

      let headerOffset = 0;
      for (let i = 0; i < headerIdx; i++) {
        headerOffset += parent.child(i).nodeSize;
      }
      tr.setNodeMarkup(parentOffset + headerOffset, undefined, {
        ...headerNode.attrs,
        pricingType: newType,
      });

      let tableOffset = 0;
      for (let i = 0; i < tableIdx; i++) {
        tableOffset += parent.child(i).nodeSize;
      }
      const tableNode = parent.child(tableIdx);
      const tableFrom = parentOffset + tableOffset;
      const tableTo = tableFrom + tableNode.nodeSize;

      const L = getInvestmentTableLabels(readGroupLabelLanguage());
      const currency = readGroupCurrency();
      const newTableJson =
        preservedRows.length > 0
          ? buildInvestmentTableJsonPreservingRows(
              newType as PricingType,
              L,
              preservedRows,
              currency
            )
          : (buildTableForPricingType(newType as PricingType, L) as Record<string, unknown>);
      tr.replaceWith(
        tr.mapping.map(tableFrom),
        tr.mapping.map(tableTo),
        editor.state.schema.nodeFromJSON(newTableJson)
      );

      if (totalIdx >= 0) {
        let totalOffset = 0;
        for (let i = 0; i < totalIdx; i++) {
          totalOffset += parent.child(i).nodeSize;
        }
        const totalNode = parent.child(totalIdx);
        const mappedFrom = tr.mapping.map(parentOffset + totalOffset);
        const mappedTo = tr.mapping.map(parentOffset + totalOffset + totalNode.nodeSize);
        const newTotalAttrs = { ...totalNode.attrs, pricingType: newType };
        tr.replaceWith(
          mappedFrom,
          mappedTo,
          editor.state.schema.nodeFromJSON({
            type: 'investmentTotalNode',
            attrs: newTotalAttrs,
          })
        );
      }

      editor.view.dispatch(tr);
    },
    [editor, getPos, attrs.tableId, readGroupLabelLanguage, readGroupCurrency, phaseTitle]
  );

  const handleSaveToLibrary = useCallback(() => {
    const tableData = extractTableDataForLibrary(
      editor,
      getPos,
      attrs.tableId as string,
      phaseTitle,
      pricingType
    );
    if (!tableData) {
      toast.error('Kon tabeldata niet uitlezen');
      return;
    }
    const sub = phaseSubtitle.trim();
    if (sub) {
      tableData.subtitle = sub;
    }
    if (pricingType === 'hourly' && attrs.showHourColumns === false) {
      tableData.showHourColumns = false;
    }
    if (attrs.showRowPrices === false) {
      tableData.showRowPrices = false;
    }
    const name = phaseTitle || 'Naamloze tabel';
    saveTableToLibrary(name, tableData);
    toast.success(`"${name}" opgeslagen in bibliotheek`);
  }, [
    editor,
    getPos,
    attrs.tableId,
    attrs.showHourColumns,
    attrs.showRowPrices,
    phaseTitle,
    phaseSubtitle,
    pricingType,
  ]);

  /** Datarij in de tabel van deze fase; rowIndex is 1-based, kopregels tellen niet mee. */
  const findDataRow = useCallback(
    (rowIndex: number): { row: PMNode; pos: number } | null => {
      if (!editor || typeof getPos !== 'function') return null;
      const pos = getPos();
      if (pos == null) return null;

      const resolved = editor.state.doc.resolve(pos);
      const parent = resolved.parent;
      const parentOffset = resolved.start(resolved.depth);

      let headerIdx = -1;
      for (let i = 0; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === 'investmentHeaderNode' && child.attrs.tableId === attrs.tableId) {
          headerIdx = i;
          break;
        }
      }
      if (headerIdx < 0) return null;

      let tableIdx = -1;
      for (let i = headerIdx + 1; i < parent.childCount; i++) {
        const child = parent.child(i);
        if (child.type.name === 'table') {
          tableIdx = i;
          break;
        }
        if (child.type.name === 'investmentHeaderNode') break;
      }
      if (tableIdx < 0) return null;

      let tablePos = parentOffset;
      for (let i = 0; i < tableIdx; i++) tablePos += parent.child(i).nodeSize;
      const tableNode = parent.child(tableIdx);

      let rowPos = tablePos + 1;
      let dataIndex = 0;
      for (let i = 0; i < tableNode.childCount; i++) {
        const row = tableNode.child(i);
        if (row.type.name === 'tableRow' && !rowHasHeaderCell(row)) {
          dataIndex++;
          if (dataIndex === rowIndex) return { row, pos: rowPos };
        }
        rowPos += row.nodeSize;
      }
      return null;
    },
    [editor, getPos, attrs.tableId]
  );

  /**
   * Schrijft één cel van een datarij in de tabel van deze fase.
   *
   * De zijbalk bewerkt dezelfde cellen als de tabel zelf; er is geen aparte
   * opslag. Auto-calc rekent het subtotaal daarna opnieuw uit.
   */
  const handleRowCellChange = useCallback(
    (rowIndex: number, cellIndex: number, value: string) => {
      if (!editor) return;
      const target = findDataRow(rowIndex);
      if (!target) return;
      setInvestmentRowCell(editor, target.row, target.pos, cellIndex, value);
    },
    [editor, findDataRow]
  );

  /**
   * Zet een productregel op een andere productversie: bijwerken (met nieuwe
   * bedragen) of negeren (bedragen blijven staan). Eén transactie, dus één undo.
   */
  const handleRowProductVersion = useCallback(
    (rowIndex: number, change: InvestmentRowVersionChange) => {
      if (!editor) return;
      const target = findDataRow(rowIndex);
      if (target) setInvestmentRowProductVersion(editor, target.row, target.pos, change);
    },
    [editor, findDataRow]
  );

  const handleShowHourColumnsChange = useCallback(
    (checked: boolean) => updateAttributes({ showHourColumns: checked }),
    [updateAttributes]
  );

  const handleShowRowPricesChange = useCallback(
    (checked: boolean) => updateAttributes({ showRowPrices: checked }),
    [updateAttributes]
  );

  const handleRowRateChange = useCallback((rowIndex: number, rateKey: string, amountCents?: number) => {
    const target = findDataRow(rowIndex);
    if (target) setInvestmentRowRate(editor, target.row, target.pos, rateKey, amountCents, readGroupCurrency());
  }, [editor, findDataRow, readGroupCurrency]);

  const handleRowShowHoursChange = useCallback((rowIndex: number, showHours: boolean) => {
    const target = findDataRow(rowIndex);
    if (target) setInvestmentRowShowHours(editor, target.row, target.pos, showHours);
  }, [editor, findDataRow]);

  /**
   * De zijbalk krijgt stabiele functie-identiteiten. Zonder deze proxies zou elke
   * transactie een "nieuwe" bediening publiceren en het paneel steeds opnieuw renderen.
   */
  const handlersRef = useRef({
    handleRowRateChange,
    handleRowShowHoursChange,
    handlePricingTypeChange,
    handleShowTotalRowChange,
    handleShowHourColumnsChange,
    handleShowRowPricesChange,
    handleRowCellChange,
    handleRowProductVersion,
    handleMovePhase,
    handleDuplicatePhase,
    handleSaveToLibrary,
    handleDeletePhase,
    handleCurrencyChange,
    handleLabelLanguageChange,
  });
  handlersRef.current = {
    handleRowRateChange,
    handleRowShowHoursChange,
    handlePricingTypeChange,
    handleShowTotalRowChange,
    handleShowHourColumnsChange,
    handleShowRowPricesChange,
    handleRowCellChange,
    handleRowProductVersion,
    handleMovePhase,
    handleDuplicatePhase,
    handleSaveToLibrary,
    handleDeletePhase,
    handleCurrencyChange,
    handleLabelLanguageChange,
  };

  const inspectorActions = useMemo(
    () => ({
      setPricingType: (value: string) => handlersRef.current.handlePricingTypeChange(value),
      setShowTotalRow: (value: boolean) => handlersRef.current.handleShowTotalRowChange(value),
      setShowHourColumns: (value: boolean) =>
        handlersRef.current.handleShowHourColumnsChange(value),
      setShowRowPrices: (value: boolean) => handlersRef.current.handleShowRowPricesChange(value),
      setRowCell: (rowIndex: number, cellIndex: number, value: string) =>
        handlersRef.current.handleRowCellChange(rowIndex, cellIndex, value),
      setRowRate: (rowIndex: number, rateKey: string, amountCents?: number) =>
        handlersRef.current.handleRowRateChange(rowIndex, rateKey, amountCents),
      setRowShowHours: (rowIndex: number, showHours: boolean) =>
        handlersRef.current.handleRowShowHoursChange(rowIndex, showHours),
      setRowProductVersion: (rowIndex: number, change: InvestmentRowVersionChange) =>
        handlersRef.current.handleRowProductVersion(rowIndex, change),
      move: (direction: 'up' | 'down') => handlersRef.current.handleMovePhase(direction),
      duplicate: () => handlersRef.current.handleDuplicatePhase(),
      saveToLibrary: () => handlersRef.current.handleSaveToLibrary(),
      remove: () => handlersRef.current.handleDeletePhase(),
      setCurrency: (value: string) => handlersRef.current.handleCurrencyChange(value),
      setLabelLanguage: (value: string) => handlersRef.current.handleLabelLanguageChange(value),
    }),
    []
  );

  /** Documentbereik van de groep waar deze kop in zit. */
  const readGroupRange = useCallback((): { id: string; from: number; to: number } | null => {
    if (!editor || typeof getPos !== 'function') return null;
    let pos: number | undefined;
    try {
      pos = getPos();
    } catch {
      return null;
    }
    if (pos == null || pos > editor.state.doc.content.size) return null;
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const groupNode = $pos.node(d);
      if (groupNode.type.name === 'investmentGroupNode') {
        const from = $pos.before(d);
        return {
          id: String(groupNode.attrs.id ?? `group-${from}`),
          from,
          to: from + groupNode.nodeSize,
        };
      }
    }
    return null;
  }, [editor, getPos]);

  /** Documentbereik van deze fase (kop + tabel + totaal). */
  const readPhaseRange = useCallback((): { from: number; to: number } | null => {
    if (!editor || typeof getPos !== 'function') return null;
    let pos: number | undefined;
    try {
      pos = getPos();
    } catch {
      return null;
    }
    if (pos == null || pos > editor.state.doc.content.size) return null;
    const resolved = editor.state.doc.resolve(pos);
    const ranges = investmentPhaseRanges(resolved.parent, resolved.start(resolved.depth));
    const mine = ranges.find((range) => range.tableId === attrs.tableId);
    return mine ? { from: mine.from, to: mine.to } : null;
  }, [editor, getPos, attrs.tableId]);

  // Groepsinstellingen (valuta, tabeltaal) publiceren zolang de cursor in de groep staat.
  const publishedGroupIdRef = useRef<string | null>(null);
  const syncGroupInspector = useCallback(() => {
    if (!isMainHeader || !editor || (!editor.isEditable && !isQuoteWorkspacePreview(editor)))
      return;
    const range = readGroupRange();
    if (!range) return;
    if (isQuoteWorkspacePreview(editor) && editor.view.dom.dataset.workspaceSelection === 'none') {
      clearGroup(range.id);
      return;
    }
    const head = editor.state.selection.head;
    if (head >= range.from && head <= range.to) {
      publishedGroupIdRef.current = range.id;
      publishGroup({
        groupId: range.id,
        currency,
        labelLanguage,
        setCurrency: inspectorActions.setCurrency,
        setLabelLanguage: inspectorActions.setLabelLanguage,
      });
    } else if (publishedGroupIdRef.current) {
      clearGroup(publishedGroupIdRef.current);
      publishedGroupIdRef.current = null;
    }
  }, [
    isMainHeader,
    editor,
    readGroupRange,
    currency,
    labelLanguage,
    inspectorActions,
    publishGroup,
    clearGroup,
  ]);

  useEffect(() => {
    if (!isMainHeader || !editor) return;
    syncGroupInspector();
    editor.on('transaction', syncGroupInspector);
    return () => {
      editor.off('transaction', syncGroupInspector);
    };
  }, [isMainHeader, editor, syncGroupInspector]);

  useEffect(() => {
    if (!isMainHeader) return;
    return () => {
      if (publishedGroupIdRef.current) {
        clearGroup(publishedGroupIdRef.current);
        publishedGroupIdRef.current = null;
      }
    };
  }, [isMainHeader, clearGroup]);

  // Fase-instellingen publiceren zolang de cursor in deze fase staat.
  const tableId = String(attrs.tableId ?? '');
  const syncPhaseInspector = useCallback(() => {
    if (
      isMainHeader ||
      !editor ||
      !tableId ||
      (!editor.isEditable && !isQuoteWorkspacePreview(editor))
    )
      return;
    const range = readPhaseRange();
    if (!range) return;
    if (isQuoteWorkspacePreview(editor) && editor.view.dom.dataset.workspaceSelection === 'none') {
      clearPhase(tableId);
      return;
    }
    const head = editor.state.selection.head;
    if (head < range.from || head > range.to) {
      clearPhase(tableId);
      return;
    }
    publishPhase({
      tableId,
      groupId: readGroupRange()?.id ?? '',
      title: phaseTitle,
      subtitle: phaseSubtitle,
      pricingType,
      showTotalRow,
      showHourColumns: attrs.showHourColumns !== false,
      showRowPrices: attrs.showRowPrices !== false,
      hasHourColumns: pricingType === 'hourly',
      phaseIndex: phasePosition.index,
      phaseCount: phasePosition.count,
      row: readRowContext(editor.state.doc, head),
      ...inspectorActions,
      setRowInvestmentProduct: (product: InvestmentProduct) =>
        updateInvestmentProduct(editor, product),
      setProductsDisplay: (display: 'collapsed' | 'expanded') => {
        const range = readPhaseRange();
        if (!range) return;
        const tr = editor.state.tr;
        editor.state.doc.nodesBetween(range.from, range.to, (node, pos) => {
          if (node.attrs.investmentProduct)
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              investmentProduct: { ...node.attrs.investmentProduct, display },
          });
        });
        if (tr.docChanged) editor.view.dispatch(tr);
      },
    });
  }, [
    isMainHeader,
    editor,
    tableId,
    readPhaseRange,
    readGroupRange,
    phaseTitle,
    phaseSubtitle,
    pricingType,
    showTotalRow,
    attrs.showHourColumns,
    attrs.showRowPrices,
    phasePosition,
    inspectorActions,
    publishPhase,
    clearPhase,
  ]);

  useEffect(() => {
    if (isMainHeader || !editor) return;
    syncPhaseInspector();
    editor.on('transaction', syncPhaseInspector);
    return () => {
      editor.off('transaction', syncPhaseInspector);
    };
  }, [isMainHeader, editor, syncPhaseInspector]);

  useEffect(() => {
    if (isMainHeader || !tableId) return;
    return () => clearPhase(tableId);
  }, [isMainHeader, tableId, clearPhase]);

  if (isMainHeader) {
    return (
      <NodeViewWrapper as="div" className="investment-main-header">
        <div className="investment-main-header-row">
          {canEditText ? (
            <input
              value={mainTitle}
              onChange={(e) => updateAttributes({ mainTitle: e.target.value })}
              placeholder="INVESTERINGSOVERZICHT"
              aria-label="Titel investeringsoverzicht"
              className="investment-main-title"
              style={{
                fontFamily: uboTheme.fonts.primaryFallback,
                color: titleColor,
              }}
            />
          ) : (
            <h2
              className="investment-main-title"
              style={{ fontFamily: uboTheme.fonts.primaryFallback, color: titleColor }}
            >
              {mainTitle}
            </h2>
          )}
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="div"
      ref={phaseHeaderRef}
      className="investment-phase-header"
      data-investment-table-id={tableId}
      data-investment-hours-hidden={
        pricingType === 'hourly' && attrs.showHourColumns === false ? 'true' : undefined
      }
    >
      <div className="investment-phase-header-row">
        <div className="investment-phase-copy">
          {canEditText ? (
            <>
              <input
                value={phaseTitle}
                aria-label="Fasetitel"
                onChange={(e) => updateAttributes({ phaseTitle: e.target.value })}
                placeholder="Titel investeringsoverzicht..."
                className="investment-phase-title"
                style={{
                  fontFamily: uboTheme.fonts.primaryFallback,
                  color: titleColor,
                }}
              />
              {phaseSubtitle || addingSubtitle || subtitleFocused ? (
                <input
                  value={phaseSubtitle}
                  aria-label="Subtitel fase"
                  autoFocus={addingSubtitle}
                  onChange={(e) => updateAttributes({ phaseSubtitle: e.target.value })}
                  onFocus={() => setSubtitleFocused(true)}
                  onBlur={() => {
                    setSubtitleFocused(false);
                    setAddingSubtitle(false);
                  }}
                  placeholder="Subtitel (b.v. per maand, jaarlijks gefactureerd)…"
                  className="investment-phase-subtitle"
                />
              ) : null}
            </>
          ) : (
            <>
              <h3
                className="investment-phase-title"
                style={{ fontFamily: uboTheme.fonts.primaryFallback, color: titleColor }}
              >
                {phaseTitle}
              </h3>
              {phaseSubtitle ? <p className="investment-phase-subtitle">{phaseSubtitle}</p> : null}
            </>
          )}
        </div>
        {isEditable ? (
          // Even hoog als de titelregel: de bediening maakt de kop niet hoger dan voor de klant.
          <div className="investment-phase-actions">
            <span className="investment-phase-meta">{pricingTypes[pricingType]?.label ?? ''}</span>
            {canEditText && !phaseSubtitle && !addingSubtitle && !subtitleFocused ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1.5 text-muted-foreground print:hidden"
                data-html2canvas-ignore="true"
                title="Subtitel toevoegen, bijvoorbeeld per maand"
                onClick={() => setAddingSubtitle(true)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Subtitel
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1.5 text-muted-foreground transition-[background-color,color,transform] active:scale-[0.96] motion-reduce:transform-none motion-reduce:transition-none print:hidden"
              data-investment-duplicate={tableId}
              data-html2canvas-ignore="true"
              aria-label={`Investeringsoverzicht “${phaseTitle || 'Naamloos investeringsoverzicht'}” dupliceren`}
              title="Kopie direct hieronder toevoegen"
              onClick={handleDuplicatePhase}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
              Dupliceren
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 gap-1.5 text-muted-foreground hover:bg-red-50 hover:text-destructive transition-[background-color,color,transform] active:scale-[0.96] motion-reduce:transform-none motion-reduce:transition-none print:hidden"
              data-investment-remove={tableId}
              data-html2canvas-ignore="true"
              aria-label={`Investeringsoverzicht “${phaseTitle || 'Naamloos investeringsoverzicht'}” verwijderen`}
              onClick={handleDeletePhase}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Verwijderen
            </Button>
          </div>
        ) : null}
      </div>
      {isEditable && (
        <DeleteInvestmentOverviewDialog
          open={deleteDialogOpen}
          title={phaseTitle}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDeletePhase}
          onClose={handleDeleteDialogClose}
        />
      )}
    </NodeViewWrapper>
  );
}
